/**
 * Volumen Uint8 downsampled + proyección MIP/MinIP/AvgIP con clip box (decapado).
 * Se guarda también un buffer HU (Int16) para remapeo WW/WC rápido sin re-leer el stack.
 */
import { mpr3dViewBasis } from './mpr3dCamera'

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n))
}

function yieldTick() {
  return new Promise((r) => setTimeout(r, 0))
}

/**
 * Gauss separable 3-tap (1,2,1). Pocos ciclos = más detalle fino (cercano a MPR).
 * @param {Uint8Array} data
 * @param {number} n
 * @param {number} cycles
 */
function smoothUint8Volume(data, n, cycles = 0) {
  const passes = Math.max(0, Math.min(3, Math.round(Number(cycles) || 0)))
  if (!passes || !data || !(n > 4)) return
  const n2 = n * n
  const tmp = new Uint8Array(n * n * n)
  const pass = (src, dst, ax) => {
    for (let z = 0; z < n; z++) {
      for (let y = 0; y < n; y++) {
        for (let x = 0; x < n; x++) {
          let xm = x
          let ym = y
          let zm = z
          let xp = x
          let yp = y
          let zp = z
          if (ax === 0) {
            xm = x > 0 ? x - 1 : x
            xp = x + 1 < n ? x + 1 : x
          } else if (ax === 1) {
            ym = y > 0 ? y - 1 : y
            yp = y + 1 < n ? y + 1 : y
          } else {
            zm = z > 0 ? z - 1 : z
            zp = z + 1 < n ? z + 1 : z
          }
          const c = src[x + y * n + z * n2]
          const a = src[xm + ym * n + zm * n2]
          const b = src[xp + yp * n + zp * n2]
          dst[x + y * n + z * n2] = (a + c * 2 + b + 2) >> 2
        }
      }
    }
  }
  for (let i = 0; i < passes; i++) {
    pass(data, tmp, 0)
    pass(tmp, data, 1)
    pass(data, tmp, 2)
    data.set(tmp)
  }
}

function readVoi(image, fallback) {
  let ww = fallback && fallback.ww
  let wc = fallback && fallback.wc
  if (image) {
    if (!(ww > 0) && image.windowWidth != null) ww = Number(image.windowWidth)
    if (wc == null && image.windowCenter != null) wc = Number(image.windowCenter)
  }
  if (!(ww > 0)) ww = 400
  if (wc == null || Number.isNaN(Number(wc))) wc = 40
  const slope = image && image.slope != null ? Number(image.slope) : 1
  const intercept = image && image.intercept != null ? Number(image.intercept) : 0
  return { ww, wc, slope: slope || 1, intercept: intercept || 0 }
}

function readSpacingMm(cols, rows, zDim, opts) {
  const img = opts && opts.image
  const dx = Math.max(
    1e-6,
    Number(opts && opts.dx) ||
      Number(img && img.columnPixelSpacing) ||
      1
  )
  const dy = Math.max(
    1e-6,
    Number(opts && opts.dy) ||
      Number(img && img.rowPixelSpacing) ||
      dx
  )
  const dz = Math.max(
    1e-6,
    Number(opts && opts.dz) ||
      Number(img && img.sliceThickness) ||
      dy
  )
  return [dx, dy, dz]
}

function resolveBoxScale(cols, rows, zDim, opts) {
  const [dx, dy, dz] = readSpacingMm(cols, rows, zDim, opts)
  const sx = Math.max(1, cols) * dx
  const sy = Math.max(1, rows) * dy
  const sz = Math.max(1, zDim - 1) * dz
  const m = Math.max(sx, sy, sz, 1e-6)
  return {
    x: sx / m,
    y: sy / m,
    z: sz / m,
  }
}

function clampHu(v) {
  if (v < -32768) return -32768
  if (v > 32767) return 32767
  return (v + (v < 0 ? -0.5 : 0.5)) | 0
}

/**
 * Remapea buffer HU → Uint8 con WW/WC (sin releer DICOM).
 * @returns {boolean}
 */
export function remapVolumeWwwc(volume, ww, wc, opts = {}) {
  if (!volume || !volume.hu || !volume.data || !(volume.size > 0)) return false
  const n = volume.size
  const nextWw = Math.max(1, Number(ww) || volume.ww || 400)
  const nextWc = Number.isFinite(Number(wc)) ? Number(wc) : (volume.wc != null ? Number(volume.wc) : 40)
  const vmin = nextWc - nextWw / 2
  const scale = nextWw > 0 ? 255 / nextWw : 1
  const hu = volume.hu
  const data = volume.data
  const len = n * n * n
  for (let i = 0; i < len; i++) {
    let u = (hu[i] - vmin) * scale
    if (u < 0) u = 0
    else if (u > 255) u = 255
    data[i] = (u + 0.5) | 0
  }
  const smooth =
    opts.smoothPasses != null
      ? opts.smoothPasses
      : volume.smoothPasses != null
        ? volume.smoothPasses
        : 0
  smoothUint8Volume(data, n, smooth)
  volume.ww = nextWw
  volume.wc = nextWc
  volume.rev = (volume.rev || 0) + 1
  return true
}

/**
 * @param {Array} files stack DICOM (pixelData)
 * @param {{ n?: number, ww?: number, wc?: number, flipZ?: boolean, smoothPasses?: number, onProgress?: function, signal?: { aborted?: boolean } }} opts
 */
export async function buildUint8Volume(files, opts = {}) {
  if (!files || files.length < 3) {
    throw new Error('Stack insuficiente para 3D.')
  }
  const n = Math.max(32, Math.min(256, Math.round(Number(opts.n) || 128)))
  const f0 = files.find((f) => f && f.image && typeof f.image.getPixelData === 'function') || files[0]
  const cols = Number((f0 && (f0.columns || (f0.image && f0.image.columns))) || 0)
  const rows = Number((f0 && (f0.rows || (f0.image && f0.image.rows))) || 0)
  const zDim = files.length
  if (!(cols > 0) || !(rows > 0)) throw new Error('Sin tamaño de matriz para 3D.')

  const voi = readVoi(f0.image, opts)
  const flipZ = !!opts.flipZ
  const smoothPasses =
    opts.smoothPasses != null ? Math.max(0, Math.min(3, Number(opts.smoothPasses) || 0)) : 0
  const data = new Uint8Array(n * n * n)
  const hu = new Int16Array(n * n * n)
  await yieldTick()
  const n1 = Math.max(1, n - 1)
  const c1 = Math.max(1, cols - 1)
  const r1 = Math.max(1, rows - 1)
  const z1 = Math.max(1, zDim - 1)

  const sliceCache = []
  const readSlice = (z) => {
    const hit = sliceCache.find((s) => s.z === z)
    if (hit) return hit
    const f = files[z]
    const img = f && f.image
    if (!img || typeof img.getPixelData !== 'function') return null
    const pd = img.getPixelData()
    const rec = {
      z,
      pd,
      cols: Number(f.columns || img.columns || cols) || cols,
      rows: Number(f.rows || img.rows || rows) || rows,
      slope: img.slope != null ? Number(img.slope) : voi.slope,
      intercept: img.intercept != null ? Number(img.intercept) : voi.intercept,
    }
    sliceCache.push(rec)
    if (sliceCache.length > 5) sliceCache.shift()
    return rec
  }

  const bilinearHu = (sl, x, y) => {
    const sc = sl.cols
    const sr = sl.rows
    const pd = sl.pd
    const x0 = x <= 0 ? 0 : (x >= sc - 1 ? sc - 1 : x | 0)
    const y0 = y <= 0 ? 0 : (y >= sr - 1 ? sr - 1 : y | 0)
    const x1 = x0 + 1 < sc ? x0 + 1 : x0
    const y1 = y0 + 1 < sr ? y0 + 1 : y0
    const fx = x - x0
    const fy = y - y0
    const v00 = pd[x0 + y0 * sc]
    const v10 = pd[x1 + y0 * sc]
    const v01 = pd[x0 + y1 * sc]
    const v11 = pd[x1 + y1 * sc]
    const v = v00 * (1 - fx) * (1 - fy) + v10 * fx * (1 - fy) + v01 * (1 - fx) * fy + v11 * fx * fy
    return v * sl.slope + sl.intercept
  }

  for (let zi = 0; zi < n; zi++) {
    if (opts.signal && opts.signal.aborted) {
      throw new Error('3D cancelado.')
    }
    const zf = (zi / n1) * z1
    const zs0 = zf | 0
    const zs1 = zs0 + 1 < zDim ? zs0 + 1 : zs0
    const tz = zf - zs0
    // Stack cabeza→pies: files[0] = coronilla. Textura +Z (zi alto) = superior
    // para que la cámara (up = +Z) muestre la cabeza arriba.
    const sl0 = readSlice(zDim - 1 - zs0)
    const sl1 = tz > 0.001 ? readSlice(zDim - 1 - zs1) : sl0
    if (!sl0) continue
    const destZ = zi * n * n
    for (let yi = 0; yi < n; yi++) {
      const yf = (yi / n1) * r1
      const destY = destZ + yi * n
      for (let xi = 0; xi < n; xi++) {
        const xf = (xi / n1) * c1
        let h = bilinearHu(sl0, xf, yf)
        if (sl1 && sl1 !== sl0 && tz > 0.001) {
          h = h * (1 - tz) + bilinearHu(sl1, xf, yf) * tz
        }
        hu[destY + xi] = clampHu(h)
      }
    }
    if (opts.onProgress && (zi === n - 1 || zi === 0 || (zi & 7) === 7)) opts.onProgress(zi + 1, n)
    if (zi === 0 || (zi & 3) === 3) await yieldTick()
  }

  const volume = {
    data,
    hu,
    size: n,
    cols,
    rows,
    zDim,
    ww: voi.ww,
    wc: voi.wc,
    flipZ,
    smoothPasses,
    rev: 1,
    spacingMm: readSpacingMm(cols, rows, zDim, opts),
    boxScale: resolveBoxScale(cols, rows, zDim, opts),
  }
  remapVolumeWwwc(volume, voi.ww, voi.wc, { smoothPasses })
  return volume
}

export function volumeIndexToGrid(i, srcMax, n) {
  if (!(srcMax > 1) || !(n > 1)) return 0
  return clamp(Math.round((Number(i) || 0) / (srcMax - 1) * (n - 1)), 0, n - 1)
}

/**
 * Grilla 3D → índice fuente.
 */
export function volumeGridToIndex(g, srcMax, n) {
  if (!(srcMax > 1) || !(n > 1)) return 0
  return clamp(Math.round((Number(g) || 0) / (n - 1) * (srcMax - 1)), 0, srcMax - 1)
}

/**
 * Grilla del volumen 3D → índices MPR (espacio fuente / stack).
 */
export function volumeGridToPoint(grid, size, volume) {
  if (!grid || !volume || !volume.size) return { x: 0, y: 0, z: 0 }
  const n = volume.size
  const cols = Math.max(1, Number((size && size.cols) || volume.cols) || 1)
  const rows = Math.max(1, Number((size && size.rows) || volume.rows) || 1)
  const zDim = Math.max(1, Number((size && size.zDim) || volume.zDim) || 1)
  let z = volumeGridToIndex(grid.z, zDim, n)
  // Grilla +Z = superior; stack índice 0 = coronilla.
  if (zDim > 1) z = zDim - 1 - z
  return {
    x: volumeGridToIndex(grid.x, cols, n),
    y: volumeGridToIndex(grid.y, rows, n),
    z,
  }
}

/**
 * Índices MPR (espacio fuente / stack) → grilla del volumen 3D.
 * Stack cabeza→pies: índice 0 = coronilla; grilla +Z = superior.
 */
export function volumePointToGrid(point, size, volume) {
  if (!point || !volume || !volume.size) return { x: 0, y: 0, z: 0 }
  const n = volume.size
  const cols = Math.max(1, Number((size && size.cols) || volume.cols) || 1)
  const rows = Math.max(1, Number((size && size.rows) || volume.rows) || 1)
  const zDim = Math.max(1, Number((size && size.zDim) || volume.zDim) || 1)
  let z = Number(point.z) || 0
  if (zDim > 1) z = zDim - 1 - z
  return {
    x: volumeIndexToGrid(point.x, cols, n),
    y: volumeIndexToGrid(point.y, rows, n),
    z: volumeIndexToGrid(z, zDim, n),
  }
}

/**
 * Proyecta el volumen a RGBA (Uint8ClampedArray).
 * clip: índices en espacio downsampled; keep +1 = conservar >= clip (decapado).
 */
export function projectMip(volume, opts = {}) {
  if (!volume || !volume.data) return null
  const N = volume.size
  const data = volume.data
  const w = Math.max(32, Math.min(512, Math.round(Number(opts.width) || 256)))
  const h = Math.max(32, Math.min(512, Math.round(Number(opts.height) || 256)))
  const yaw = Number(opts.yaw) || 0
  const pitch = opts.pitch != null ? Number(opts.pitch) : 0.18
  const zoom = Math.max(0.35, Math.min(4.2, Number(opts.zoom) || 1))
  const panX = Number(opts.panX) || 0
  const panY = Number(opts.panY) || 0
  const mode = opts.mode === 'minip' || opts.mode === 'avg' ? opts.mode : 'mip'
  const clip = opts.clip
  const decapado = !!(opts.decapado && clip)
  const keep = opts.keep || { x: 1, y: 1, z: 1 }
  const mask = opts.clipMask || { x: 1, y: 1, z: 1 }
  const slab = Math.max(0, Number(opts.slab) || 0)
  const fast = !!opts.fast
  const lut = opts.lut === 'gray' ? 'gray' : 'bone'

  const cx0 = clip && clip.x != null ? clip.x | 0 : 0
  const cy0 = clip && clip.y != null ? clip.y | 0 : 0
  const cz0 = clip && clip.z != null ? clip.z | 0 : 0
  const kx = keep.x >= 0 ? 1 : -1
  const ky = keep.y >= 0 ? 1 : -1
  const kz = keep.z >= 0 ? 1 : -1

  const { fx, fy, fz, rx, ry, rz, ux, uy, uz } = mpr3dViewBasis(
    yaw,
    pitch,
    !!opts.flipX,
    !!opts.flipY
  )

  const mid = (N - 1) * 0.5
  const aspect = h > 0 ? w / h : 1
  const fov = N / zoom
  const steps = fast ? Math.max(28, Math.round(N * 0.55)) : Math.round(N * 1.35)
  const stepLen = (Math.sqrt(3) * N) / Math.max(1, steps)
  const dist = N * 1.55
  const pixels = new Uint8ClampedArray(w * h * 4)

  const insideClip = (xi, yi, zi) => {
    if (!decapado) return true
    if (slab > 0.5) {
      if (mask.x > 0 && Math.abs(xi - cx0) > slab) return false
      if (mask.y > 0 && Math.abs(yi - cy0) > slab) return false
      if (mask.z > 0 && Math.abs(zi - cz0) > slab) return false
      return true
    }
    if (mask.x > 0) {
      if (kx > 0 && xi < cx0) return false
      if (kx < 0 && xi > cx0) return false
    }
    if (mask.y > 0) {
      if (ky > 0 && yi < cy0) return false
      if (ky < 0 && yi > cy0) return false
    }
    if (mask.z > 0) {
      if (kz > 0 && zi < cz0) return false
      if (kz < 0 && zi > cz0) return false
    }
    return true
  }

  for (let py = 0; py < h; py++) {
    const v = (0.5 - (py + 0.5) / h - panY) * fov
    for (let px = 0; px < w; px++) {
      const u = ((px + 0.5) / w - 0.5 - panX / Math.max(aspect, 0.001)) * fov * aspect
      let x = mid - fx * dist + rx * u + ux * v
      let y = mid - fy * dist + ry * u + uy * v
      let z = mid - fz * dist + rz * u + uz * v
      let acc = mode === 'minip' ? 255 : 0
      let count = 0
      let hit = false
      for (let s = 0; s < steps; s++) {
        const xi = x | 0
        const yi = y | 0
        const zi = z | 0
        if (xi >= 0 && yi >= 0 && zi >= 0 && xi < N && yi < N && zi < N && insideClip(xi, yi, zi)) {
          const val = data[xi + yi * N + zi * N * N]
          hit = true
          if (mode === 'mip') {
            if (val > acc) acc = val
          } else if (mode === 'minip') {
            if (val < acc) acc = val
          } else {
            acc += val
            count++
          }
        }
        x += fx * stepLen
        y += fy * stepLen
        z += fz * stepLen
      }
      if (mode === 'avg') acc = count ? acc / count : 0
      if (!hit) acc = 0
      const o = (py * w + px) * 4
      const g = acc | 0
      if (lut === 'gray') {
        pixels[o] = g
        pixels[o + 1] = g
        pixels[o + 2] = g
      } else {
        // LUT 1D tipo hueso (no es textura 3D): marfil en altos, cálido en medios
        pixels[o] = g
        pixels[o + 1] = (g * 0.86) | 0
        pixels[o + 2] = (g * 0.62) | 0
      }
      pixels[o + 3] = 255
    }
  }
  return { pixels, width: w, height: h }
}

export function disposeVolume(volume) {
  if (!volume) return
  volume.data = null
  volume.hu = null
}
